/**
 * BALANCE.0A — current neutral weapon/reference progression fixtures.
 *
 * This module owns weapon/subtype identity, level/reference allocation
 * fixtures, and genuinely subtype-specific raw source data only. Family
 * reference raw source values are owned exclusively by ACTIVE.1's
 * `WEAPON_PRIMARY_PROFILE_V0.referenceSources` and are consumed from there at
 * resolution time; they are never copied into this module.
 */

import type { PrimaryStatKey, PrimaryStats } from '../sim/primary-stats.js';
import { ZERO_PRIMARY_STATS } from '../sim/primary-stats.js';
import type { CombatSourceDefinition } from '../sim/source-profile.js';
import { WEAPON_PRIMARY_PROFILE_V0, type WeaponPrimaryProfile } from '../sim/weapon-primary-profile.js';
import type { WeaponFamily } from '../types.js';
import { V13_EXPERIMENTAL_ITEM_WEAPON_POWER_MULTIPLIER } from '../calibration/skill-damage-v13.js';
import { INTRINSIC_MAGIC_BASE_DAMAGE_RATIO, INTRINSIC_MAGIC_BASIC_ACTION_CYCLE_MULTIPLIER } from '../calibration/magic-source-v1.js';
import type { ReferenceProgressionBand } from './types.js';

export type ReferenceWeaponLevel = 20 | 60 | 100;
export type PrimaryAllocation = Readonly<Partial<Record<PrimaryStatKey, number>>>;

export type BalanceWeaponId =
  | 'DAGGER_PAIR'
  | 'SWORD'
  | 'HEAVY_AXE'
  | 'SPEAR'
  | 'BOW_REFERENCE'
  | 'ASSASSIN_BLADE'
  | 'GREATSWORD';

export type CatalystWeaponId = 'ARCANE_CATALYST' | 'BLAST_CATALYST' | 'RAPID_CATALYST';
export type PlayableWeaponId = BalanceWeaponId | CatalystWeaponId;
export const CATALYST_WEAPON_IDS = Object.freeze(['ARCANE_CATALYST', 'BLAST_CATALYST', 'RAPID_CATALYST'] as const);
export function isCatalystWeaponId(value: string): value is CatalystWeaponId {
  return (CATALYST_WEAPON_IDS as readonly string[]).includes(value);
}

/**
 * `LIGHT_REFERENCE`, `HEAVY_REFERENCE`, `RANGED_REFERENCE` and `CATALYST_REFERENCE` are deliberately NOT
 * `BalanceWeaponId`s: that union is the set of weapons the Balance Lab combat
 * kernel has ratified spatial geometry (`sim/basic-attack-geometry.ts`) and
 * basic-attack timing (`sim/basic-attack-timing.ts`) for. Both of these are
 * calibration stand-ins for a whole family, not swingable weapons, so neither
 * carries — nor should invent — that geometry.
 */
export const FAMILY_REFERENCE_WEAPON_IDS = Object.freeze(['CATALYST_REFERENCE', 'LIGHT_REFERENCE', 'HEAVY_REFERENCE', 'RANGED_REFERENCE'] as const);
export type FamilyReferenceWeaponId = typeof FAMILY_REFERENCE_WEAPON_IDS[number];
export type PrimaryStatV0WeaponId = BalanceWeaponId | FamilyReferenceWeaponId;

/**
 * What a craft may be priced on (`pricingWeaponId`): a concrete balance weapon,
 * or `CATALYST_REFERENCE` — the one fixed basis every MAGIC craft shares, so a
 * MAGIC skill costs the same whatever the crafter holds.
 */
export type PricingWeaponId = BalanceWeaponId | 'CATALYST_REFERENCE';

export interface FamilyReferenceWeaponProgressionFixture {
  readonly weaponId: PrimaryStatV0WeaponId;
  readonly family: WeaponFamily;
  /**
   * Raw source values come from
   * `WEAPON_PRIMARY_PROFILE_V0.referenceSources[family]`.
   */
  readonly sourceKind: 'FAMILY_REFERENCE';
}

export interface SubtypeWeaponProgressionFixture {
  readonly weaponId: PrimaryStatV0WeaponId;
  readonly family: WeaponFamily;
  readonly sourceKind: 'SUBTYPE';
  /** Level-20 raw source damage before Primary scaling. */
  readonly rawBaseDamageLv20: number;
  /** Raw source attack rate before Primary handling scaling. */
  readonly rawBaseAttackRatePerS: number;
}

/**
 * A playable weapon whose raw source is its family's `referenceSources` entry
 * times a basic-attack tuning multiplier. Resolved against the supplied
 * profile, so a caller-overridden `referenceSources` is honoured.
 */
export interface TunedFamilyReferenceWeaponProgressionFixture {
  readonly weaponId: PrimaryStatV0WeaponId;
  readonly family: WeaponFamily;
  readonly sourceKind: 'TUNED_FAMILY_REFERENCE';
  readonly baseDamageMultiplier: number;
  readonly baseAttackRateMultiplier: number;
}

export type WeaponProgressionFixture =
  | FamilyReferenceWeaponProgressionFixture
  | SubtypeWeaponProgressionFixture
  | TunedFamilyReferenceWeaponProgressionFixture;

/**
 * Reference weapon identities point at family authority without repeating raw
 * values. Only actual subtype-specific PR29/PR26 fixtures retain raw source
 * literals here.
 */
export const WEAPON_PROGRESSION_V0: Readonly<Record<PrimaryStatV0WeaponId, WeaponProgressionFixture>> = Object.freeze({
  DAGGER_PAIR: Object.freeze({
    weaponId: 'DAGGER_PAIR',
    family: 'LIGHT',
    sourceKind: 'SUBTYPE',
    rawBaseDamageLv20: 44 / 1.108,
    rawBaseAttackRatePerS: 1.674,
  }),
  /**
   * The LIGHT family reference. Every other family had one
   * (`SWORD`/`HEAVY_AXE`/`SPEAR`/`BOW_REFERENCE`/`CATALYST_REFERENCE`); LIGHT
   * had only subtypes (`DAGGER_PAIR`, `ASSASSIN_BLADE`), so
   * `reference-context.ts`'s `FAMILY_REFERENCE_WEAPON_ID.LIGHT` pointed at the
   * `DAGGER_PAIR` **subtype** as a stand-in. That subtype carries
   * `rawBaseDamageLv20 = 44 / 1.108 = 39.71` against the family's own
   * `referenceSources.LIGHT.baseDamage = 26.2` — 1.516x — so anything measured
   * through the family reference evaluated LIGHT at 1.66x the other families'
   * basic DPS (220.8 vs ~132) instead of the intended 1.10x, and
   * `resolveCadenceCalibration` (which reads `referenceSources` directly)
   * and the evaluation source state disagreed about what a LIGHT weapon is
   * inside one pipeline.
   */
  LIGHT_REFERENCE: Object.freeze({ weaponId: 'LIGHT_REFERENCE', family: 'LIGHT', sourceKind: 'FAMILY_REFERENCE' }),
  SWORD: Object.freeze({ weaponId: 'SWORD', family: 'BALANCED', sourceKind: 'FAMILY_REFERENCE' }),
  /**
   * The HEAVY family reference, split from the playable `HEAVY_AXE` the same
   * way as LIGHT: `HEAVY_AXE` carries the basic-attack rate tuning (x0.81) in
   * its own raw source, and generation calibration must keep reading the
   * untuned `referenceSources.HEAVY`.
   */
  HEAVY_REFERENCE: Object.freeze({ weaponId: 'HEAVY_REFERENCE', family: 'HEAVY', sourceKind: 'FAMILY_REFERENCE' }),
  HEAVY_AXE: Object.freeze({
    weaponId: 'HEAVY_AXE',
    family: 'HEAVY',
    sourceKind: 'TUNED_FAMILY_REFERENCE',
    baseDamageMultiplier: 1,
    baseAttackRateMultiplier: 0.81,
  }),
  SPEAR: Object.freeze({ weaponId: 'SPEAR', family: 'POLEARM', sourceKind: 'FAMILY_REFERENCE' }),
  /** The RANGED family reference; `BOW_REFERENCE` carries the x1.08 rate tuning (see `HEAVY_REFERENCE`). */
  RANGED_REFERENCE: Object.freeze({ weaponId: 'RANGED_REFERENCE', family: 'RANGED', sourceKind: 'FAMILY_REFERENCE' }),
  BOW_REFERENCE: Object.freeze({
    weaponId: 'BOW_REFERENCE',
    family: 'RANGED',
    sourceKind: 'TUNED_FAMILY_REFERENCE',
    baseDamageMultiplier: 1,
    baseAttackRateMultiplier: 1.08,
  }),
  ASSASSIN_BLADE: Object.freeze({
    weaponId: 'ASSASSIN_BLADE',
    family: 'LIGHT',
    sourceKind: 'SUBTYPE',
    rawBaseDamageLv20: (120 / 1.108) * 1.067 * 1.05,
    rawBaseAttackRatePerS: (1 / 2.60) / 1.105,
  }),
  GREATSWORD: Object.freeze({
    weaponId: 'GREATSWORD',
    family: 'HEAVY',
    sourceKind: 'SUBTYPE',
    rawBaseDamageLv20: (170 / 1.12) * 0.72,
    rawBaseAttackRatePerS: (0.40 / 1.095) * 0.63,
  }),
  CATALYST_REFERENCE: Object.freeze({ weaponId: 'CATALYST_REFERENCE', family: 'CATALYST', sourceKind: 'FAMILY_REFERENCE' }),
});

export const REFERENCE_LEVEL_TO_BAND: Readonly<Record<ReferenceWeaponLevel, ReferenceProgressionBand>> = Object.freeze({
  20: 'novice',
  60: 'intermediate',
  100: 'advanced',
});

/** Issue #28: Catalyst basic mana is equipment/progression-owned, not character-level repricing. */
export const CATALYST_BASIC_MANA_COST_BY_LEVEL: Readonly<Record<ReferenceWeaponLevel, number>> = Object.freeze({
  20: 3,
  60: 5,
  100: 8,
});

export function catalystBasicManaCost(level: ReferenceWeaponLevel): number {
  return CATALYST_BASIC_MANA_COST_BY_LEVEL[level];
}

/** Current 5-points/level reference allocation shape, not a player-build rule. */
export const PRIMARY_V0_CANONICAL_3_1_1_SHAPE_BY_LEVEL: Readonly<Record<ReferenceWeaponLevel, readonly [number, number, number]>> = Object.freeze({
  20: Object.freeze([60, 20, 20] as const),
  60: Object.freeze([180, 60, 60] as const),
  100: Object.freeze([300, 100, 100] as const),
});

/** PR29 reference build ordering. This is fixture ownership, not affinity ownership. */
export const PRIMARY_V0_CANONICAL_STAT_ORDER: Readonly<Record<WeaponFamily, readonly [PrimaryStatKey, PrimaryStatKey, PrimaryStatKey]>> = Object.freeze({
  HEAVY: Object.freeze(['STR', 'VIT', 'TEC'] as const),
  BALANCED: Object.freeze(['STR', 'TEC', 'AGI'] as const),
  LIGHT: Object.freeze(['AGI', 'TEC', 'STR'] as const),
  POLEARM: Object.freeze(['STR', 'TEC', 'AGI'] as const),
  RANGED: Object.freeze(['TEC', 'AGI', 'STR'] as const),
  CATALYST: Object.freeze(['MAG', 'TEC', 'WIL'] as const),
});

export function primaryAllocationForShape(
  family: WeaponFamily,
  shape: readonly [number, number, number],
): PrimaryAllocation {
  const [a, b, c] = PRIMARY_V0_CANONICAL_STAT_ORDER[family];
  return Object.freeze({ [a]: shape[0], [b]: shape[1], [c]: shape[2] });
}

export function canonicalPrimaryAllocation(family: WeaponFamily, level: ReferenceWeaponLevel): PrimaryAllocation {
  return primaryAllocationForShape(family, PRIMARY_V0_CANONICAL_3_1_1_SHAPE_BY_LEVEL[level]);
}

export function completePrimaryStats(allocation: PrimaryAllocation): PrimaryStats {
  return {
    ...ZERO_PRIMARY_STATS,
    ...allocation,
  };
}

export function itemWeaponPowerMultiplier(level: ReferenceWeaponLevel): number {
  return V13_EXPERIMENTAL_ITEM_WEAPON_POWER_MULTIPLIER[REFERENCE_LEVEL_TO_BAND[level]];
}

function rawSourceForFixture(
  fixture: WeaponProgressionFixture,
  weaponPrimaryProfile: WeaponPrimaryProfile = WEAPON_PRIMARY_PROFILE_V0,
): Readonly<{ baseDamage: number; baseAttackRate: number }> {
  if (fixture.sourceKind === 'SUBTYPE') {
    return Object.freeze({
      baseDamage: fixture.rawBaseDamageLv20,
      baseAttackRate: fixture.rawBaseAttackRatePerS,
    });
  }
  const reference = weaponPrimaryProfile.referenceSources[fixture.family];
  if (fixture.sourceKind === 'TUNED_FAMILY_REFERENCE') {
    return Object.freeze({
      baseDamage: reference.baseDamage * fixture.baseDamageMultiplier,
      baseAttackRate: reference.baseAttackRate * fixture.baseAttackRateMultiplier,
    });
  }
  return Object.freeze({
    baseDamage: reference.baseDamage,
    baseAttackRate: reference.baseAttackRate,
  });
}

/**
 * Builds the exact source definition consumed by ACTIVE.1's
 * `resolveSourceProfile()`. Family reference weapons read their raw source
 * directly from `WEAPON_PRIMARY_PROFILE_V0.referenceSources`; subtype-specific
 * weapons read the local subtype fixture. Level progression is a separate
 * V13-owned reference fixture and does not become another Weapon×Primary
 * formula.
 */
export function combatSourceAtLevel(weaponId: PrimaryStatV0WeaponId, level: ReferenceWeaponLevel): CombatSourceDefinition {
  return combatSourceAtProgression(weaponId, level, itemWeaponPowerMultiplier(level));
}

/** Builds a source from a profile-declared, validated progression level. */
export function combatSourceAtProgression(
  weaponId: PrimaryStatV0WeaponId,
  level: number,
  weaponPowerMultiplier: number,
  weaponPrimaryProfile: WeaponPrimaryProfile = WEAPON_PRIMARY_PROFILE_V0,
): CombatSourceDefinition {
  const fixture = WEAPON_PROGRESSION_V0[weaponId];
  const raw = rawSourceForFixture(fixture, weaponPrimaryProfile);
  return Object.freeze({
    id: `balance.${weaponId.toLowerCase()}.lv${level}`,
    family: fixture.family,
    weaponId,
    baseDamage: raw.baseDamage * weaponPowerMultiplier,
    baseAttackRate: raw.baseAttackRate,
  });
}

/**
 * SOURCE.2 Task 10 (source-weapon-technique-hybrid-contract.md §28) — the
 * intrinsic MAGIC basic-attack reference identity:
 * `Reference Basis = MAGIC`, `Damage Type = ARCANE`, `source = intrinsic
 * MAGIC`, single target, no free status. This is reference/calibration
 * evidence only — it does **not** wire a live basic-attack input/payment
 * scheduler (Issue #28's audit already records that Catalyst basic payment
 * loop as a separate readiness gap; this fixture does not close it).
 */
export interface IntrinsicMagicBasicReferenceFixture {
  readonly level: ReferenceWeaponLevel;
  /** `0.80 × same-tier Catalyst reference baseDamage` (contract §24/§28). */
  readonly baseDamage: number;
  /** Damage type for the intrinsic reference basic. */
  readonly damageType: 'arcane';
  /** Reuses the existing Catalyst basic mana progression verbatim (§28). */
  readonly basicManaCost: number;
  /** `1.25 × same-tier Catalyst basic cycle`, independent of per-skill influence metadata. */
  readonly intrinsicBasicActionCycleS: number;
}

export function intrinsicMagicBasicReferenceFixture(level: ReferenceWeaponLevel): IntrinsicMagicBasicReferenceFixture {
  const catalyst = combatSourceAtLevel('CATALYST_REFERENCE', level);
  const catalystBasicCycleS = 1 / catalyst.baseAttackRate;
  return Object.freeze({
    level,
    baseDamage: catalyst.baseDamage * INTRINSIC_MAGIC_BASE_DAMAGE_RATIO,
    damageType: 'arcane',
    basicManaCost: catalystBasicManaCost(level),
    intrinsicBasicActionCycleS: catalystBasicCycleS * INTRINSIC_MAGIC_BASIC_ACTION_CYCLE_MULTIPLIER,
  });
}
